const map = require('./songMap.json');
const { GAME_CONFIG, getStageForElapsedTime } = require('./config');
const { getVariantDifficulty } = require('./songDifficulty');
const STEM_UNLOCK = { drums: 0, bass: 20, music: 45 };
const STEM_LEVEL = { drums: 0.65, bass: 0.55, music: 0.5 };

function buildSongChart(sixteenthInterval, seed = 0) {
  const speed = 60 / GAME_CONFIG.bpm / 4 / sixteenthInterval;
  if (!Number.isFinite(speed) || speed <= 0) throw new Error('Invalid song speed');
  const result = [];
  const last = { left: -Infinity, right: -Infinity };
  const occupiedUntil = { left: -Infinity, right: -Infinity };
  const lead = GAME_CONFIG.rhythm.songLeadSeconds;
  let lastHit = -Infinity;
  // Begin phrases at the first strong drum beat, not at the leading silence.
  const firstBeat = map.beats.findIndex(time => time >= map.tracks.drums.onsets[0].time - 0.001);
  for (let cycle = 0; (cycle * map.duration - lead) / speed < GAME_CONFIG.time.maxDuration; cycle++) {
    const candidates = [];
    for (const [stem, track] of Object.entries(map.tracks)) {
      for (const onset of track.onsets) {
        const time = (cycle * map.duration + onset.time - lead) / speed;
        if (time < STEM_UNLOCK[stem] || time >= GAME_CONFIG.time.maxDuration - 0.6) continue;
        candidates.push({ time, stem, strength: onset.strength });
      }
    }
    candidates.sort((a, b) => a.time - b.time || b.strength - a.strength);
    const used = new Set();
    for (let phrase = firstBeat; phrase < map.beats.length - 1; phrase += 8) {
      const phraseTime = (cycle * map.duration + map.beats[phrase] - lead) / speed;
      const phraseIndex = Math.floor((phrase - firstBeat) / 8);
      const difficulty = getVariantDifficulty(phraseTime, seed);
      for (const [index, step] of difficulty.steps.entries()) {
        const beat = phrase + Math.floor(step / 4);
        if (beat + 1 >= map.beats.length) break;
        const sourceTime = map.beats[beat] + (map.beats[beat + 1] - map.beats[beat]) * (step % 4) / 4;
        const target = (cycle * map.duration + sourceTime - lead) / speed;
        if (target >= GAME_CONFIG.time.maxDuration - 0.6) break;
        let lane = difficulty.lanes[index % difficulty.lanes.length];
        // During a hold the free hand keeps playing; never require a new
        // press from a finger that is still sustaining a note.
        if (target < occupiedUntil[lane]) lane = lane === 'left' ? 'right' : 'left';
        // Select a real attack near the phrase slot, never move audio or create
        // notes in silence. Narrow windows keep neighboring slots distinct.
        const tolerance = Math.min(0.075, (map.beats[beat + 1] - map.beats[beat]) * 0.22) / speed;
        let low = 0, high = candidates.length;
        while (low < high) {
          const mid = (low + high) >>> 1;
          if (candidates[mid].time < target - tolerance) low = mid + 1;
          else high = mid;
        }
        let selected = null, distance = Infinity;
        for (let cursor = low; cursor < candidates.length && candidates[cursor].time <= target + tolerance; cursor++) {
          const event = candidates[cursor];
          if (used.has(event) || !difficulty.stems.includes(event.stem)
            || event.time < occupiedUntil[lane]
            || event.time - last[lane] < difficulty.laneGap || event.time - lastHit < difficulty.gap) continue;
          const delta = Math.abs(event.time - target);
          if (delta < distance) { selected = event; distance = delta; }
        }
        if (!selected) continue;
        used.add(selected);
        const otherLane = lane === 'left' ? 'right' : 'left';
        const double = difficulty.doubles.includes(step) && phraseIndex % difficulty.doubleEvery === 0
          && selected.time >= occupiedUntil[otherLane]
          && selected.time - last[otherLane] >= difficulty.laneGap;
        const lanes = double ? ['left', 'right'] : [lane];
        let holdDuration = 0;
        if (!double && difficulty.holdEvery && step === difficulty.holdStep && phraseIndex % difficulty.holdEvery === 0) {
          const endBeat = beat + difficulty.holdBeats;
          if (endBeat < map.beats.length) {
            const end = (cycle * map.duration + map.beats[endBeat] - lead) / speed;
            if (end < GAME_CONFIG.time.maxDuration - 0.6 && end - selected.time >= 0.25) {
              holdDuration = end - selected.time;
              occupiedUntil[lane] = end + 0.12;
            }
          }
        }
        lanes.forEach(value => { last[value] = selected.time; });
        lastHit = selected.time;
        result.push({ tick: result.length, targetHitTime: selected.time,
          stageId: getStageForElapsedTime(selected.time).id, lanes,
          difficulty: difficulty.label, phraseStep: step,
          stem: selected.stem, holdTicks: holdDuration ? difficulty.holdBeats * 4 : 0, holdDuration });
      }
    }
  }
  return result;
}

function songBeatPhase(time, speed) {
  const position = (((time * speed + GAME_CONFIG.rhythm.songLeadSeconds) % map.duration) + map.duration) % map.duration;
  let low = 0, high = map.beats.length - 1;
  while (low + 1 < high) {
    const mid = (low + high) >>> 1;
    if (map.beats[mid] <= position) low = mid;
    else high = mid;
  }
  return (position - map.beats[low]) / (map.beats[high] - map.beats[low]);
}

module.exports = { map, STEM_UNLOCK, STEM_LEVEL, buildSongChart, songBeatPhase };
